import { isNil, isNumber } from 'lodash';
import * as Yup from 'yup';

import { PayoutDistributionRange } from 'types';

type Translate = (key: string) => string;

type Interval = { min: number; max: number };

/**
 * Overlapping ranges are allowed — an order matching several of them still goes manual — but
 * usually mean a typo, so the form warns about them. Rows without a lower bound are skipped.
 * Bounds are inclusive, so adjacent ranges like 0–1000 and 1000–2000 share 1000; that is the
 * expected way to chain ranges, so only ranges sharing more than one amount are reported.
 */
const hasOverlappingRanges = (ranges?: PayoutDistributionRange[]) => {
  const intervals = (ranges ?? []).reduce<Interval[]>(
    (acc, { minAmount, maxAmount }) => {
      if (isNumber(minAmount)) {
        acc.push({
          min: minAmount,
          max: isNumber(maxAmount) ? maxAmount : Infinity,
        });
      }
      return acc;
    },
    [],
  );

  return intervals.some((a, i) =>
    intervals.some((b, j) => i < j && a.min < b.max && b.min < a.max),
  );
};

/**
 * Ranges come with the shop card as entities (id, shopId, timestamps), but the shop save accepts only
 * the bounds and rejects any other field, so the form keeps just them.
 */
const toValues = (
  ranges?: PayoutDistributionRange[],
): PayoutDistributionRange[] =>
  (ranges ?? []).map(({ minAmount, maxAmount }) => ({ minAmount, maxAmount }));

const emptyToNull = (value: unknown, originalValue: unknown) =>
  originalValue === '' ? null : value;

/**
 * Ranges are arbitrary: gaps and overlaps are fine, each row only has to be a non-empty interval.
 * As in the anti-fraud rules, both bounds are inclusive (max = min is a one-amount range) and only
 * an empty max means no upper bound — 0 is a literal bound.
 * Validated only while the shop's manual distribution flag is on: with it off the editor is hidden,
 * so nothing in it may block saving the shop.
 */
const getValidationSchema = (t: Translate, tCommon: Translate) =>
  Yup.array().when(
    'payoutManualDistribution',
    ([payoutManualDistribution], schema) =>
      payoutManualDistribution
        ? schema.of(
            Yup.object().shape({
              minAmount: Yup.number()
                .transform(emptyToNull)
                .nullable()
                .required(tCommon('errors.required'))
                .min(0),
              maxAmount: Yup.number()
                .transform(emptyToNull)
                .nullable()
                .optional()
                .test(
                  'not-below-min-amount',
                  t(
                    'sections.payout_distribution_ranges.errors.invalid_bounds',
                  ),
                  (maxAmount, context) =>
                    isNil(maxAmount) ||
                    isNil(context.parent.minAmount) ||
                    maxAmount >= context.parent.minAmount,
                ),
            }),
          )
        : schema,
  );

export const payoutDistributionRangesUtils = {
  hasOverlappingRanges,
  toValues,
  getValidationSchema,
};
